/**
 * @fileoverview Live import progress display for the system-web
 *              DepartmentParcelDataTab. Shows real-time parcel counts
 *              (processed / total) and a progress bar as the Regrid
 *              ingestion worker processes parcels.
 * @module system-web/components/ImportProgressDisplay
 *
 * Responsibilities:
 * - Render the active regridIngestion background job's progress.
 * - Show parcels processed / total with a visual progress bar.
 * - Show the current phase (queued, starting, importing, completed, failed).
 * - Auto-transition to the import history view when the job completes.
 *
 * Dependencies:
 * - @firesquirrel/local-db (BackgroundJob type).
 * - @ui-admin/contexts/ThemeContext.
 * - lucide-react icons.
 *
 * Architecture:
 * - Pure presentational component — receives the BackgroundJob from the
 *   parent hook's liveQuery subscription. No direct API or Dexie calls.
 *
 * Notes:
 * - The progress data comes from the worker's updateProgress callback,
 *   which updates the backgroundJobs MongoDB document. The change stream
 *   → WebSocket → sync pull pipeline propagates the update to Dexie,
 *   which the hook's liveQuery picks up.
 * - No mock data, no hardcoded URLs.
 *
 * @component
 * Purpose:     Show live Regrid import progress (parcels processed / total).
 * Props:       job — the active BackgroundJob from the hook.
 * Behavior:    Renders a progress bar and live parcel counts.
 * Data Source: local (Dexie via parent hook's liveQuery).
 * Constraints: pure presentational — no API or Dexie calls.
 */

import { useState } from 'react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, Database, Ban, X,
} from 'lucide-react';
import type { BackgroundJob } from '@firesquirrel/local-db';

interface Props {
  job: BackgroundJob | null;
  /** Optional cancel handler. When provided, a Cancel button is shown for
   * pending/running jobs so an admin can dismiss a stuck import. */
  onCancel?: () => Promise<boolean> | boolean;
  /** Optional dismiss handler. When provided, an X button is shown for
   * terminal-state jobs (failed/cancelled/completed) so the user can clear
   * the progress display after reading the result. */
  onDismiss?: () => void;
}

/**
 * @component ImportProgressDisplay
 *
 * Purpose:
 * - Render the live progress of an active Regrid parcel import job.
 *
 * Props:
 * - job: The BackgroundJob from the hook's liveQuery subscription. When
 *   null, nothing is rendered.
 *
 * Behavior:
 * - Shows a progress bar with percent, parcels processed / total, and
 *   the current phase message.
 * - Color-codes by status: blue for running, green for completed, red for
 *   failed, amber for pending/queued.
 *
 * Data Source:
 * - local (Dexie via parent hook's liveQuery).
 *
 * Constraints:
 * - Pure presentational — no API or Dexie calls.
 */
export default function ImportProgressDisplay({ job, onCancel, onDismiss }: Props) {
  const { theme } = useTheme();
  const [cancelling, setCancelling] = useState(false);

  if (!job) return null;

  const status = job.status;
  const progress = job.progress;
  const percent = progress?.percent ?? 0;
  const current = progress?.current ?? 0;
  const total = progress?.total ?? 0;
  const phase = progress?.phase ?? status;
  const message = job.message ?? '';

  const isRunning = status === 'running' || status === 'pending';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';
  const isCancelled = status === 'cancelled';

  const barColor = isCompleted
    ? 'bg-green-500'
    : isFailed || isCancelled
      ? 'bg-red-500'
      : 'bg-blue-500';

  const statusIcon = isCompleted
    ? <CheckCircle2 className="h-4 w-4 text-green-600" />
    : isFailed
      ? <XCircle className="h-4 w-4 text-red-600" />
      : isCancelled
        ? <XCircle className="h-4 w-4 text-gray-500" />
        : <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;

  const statusLabel = isCompleted
    ? 'Import Complete'
    : isFailed
      ? 'Import Failed'
      : isCancelled
        ? 'Import Cancelled'
        : status === 'pending'
          ? 'Queued'
          : 'Importing…';

  const handleCancel = async () => {
    if (!onCancel || cancelling) return;
    setCancelling(true);
    try {
      await onCancel();
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="rounded-lg p-3 border" style={{
      background: theme.expandedBg,
      borderColor: isFailed ? '#fca5a5' : isCompleted ? '#86efac' : theme.cardBorder,
    }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Database className="h-3.5 w-3.5" style={{ color: theme.textSecondary }} />
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            Regrid Parcel Import
          </h4>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs font-medium">
            {statusIcon}
            <span style={{
              color: isCompleted ? '#16a34a' : isFailed ? '#dc2626' : theme.textPrimary,
            }}>
              {statusLabel}
            </span>
          </div>
          {isRunning && onCancel && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-60"
              title="Cancel this stuck import job"
            >
              <Ban className="h-3 w-3" />
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
          {!isRunning && onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex items-center justify-center p-0.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
              title="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-xs mb-1" style={{ color: theme.textSecondary }}>
          <span>{message || `Phase: ${phase}`}</span>
          <span className="tabular-nums">{percent}%</span>
        </div>
        <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
          />
        </div>
      </div>

      {/* Live parcel counts */}
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div>
          <dt className="text-xs font-medium mb-0.5" style={{ color: theme.textSecondary }}>
            Parcels Processed
          </dt>
          <dd className="text-sm font-semibold tabular-nums" style={{ color: theme.textPrimary }}>
            {current.toLocaleString()}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium mb-0.5" style={{ color: theme.textSecondary }}>
            Total Parcels
          </dt>
          <dd className="text-sm font-semibold tabular-nums" style={{ color: theme.textPrimary }}>
            {total > 0 ? total.toLocaleString() : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium mb-0.5" style={{ color: theme.textSecondary }}>
            Remaining
          </dt>
          <dd className="text-sm font-semibold tabular-nums" style={{ color: theme.textPrimary }}>
            {total > 0 ? Math.max(0, total - current).toLocaleString() : '—'}
          </dd>
        </div>
      </div>

      {/* Error message */}
      {isFailed && job.error && (
        <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-red-50 text-red-700 text-xs rounded">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span className="break-all">{job.error}</span>
        </div>
      )}

      {/* Completed summary */}
      {isCompleted && job.payload && (
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
          {typeof job.payload.newParcelsAdded === 'number' && (
            <div>
              <dt className="font-medium mb-0.5" style={{ color: theme.textSecondary }}>New</dt>
              <dd className="font-semibold text-green-600 tabular-nums">
                {Number(job.payload.newParcelsAdded).toLocaleString()}
              </dd>
            </div>
          )}
          {typeof job.payload.ownershipChangesDetected === 'number' && (
            <div>
              <dt className="font-medium mb-0.5" style={{ color: theme.textSecondary }}>Updated</dt>
              <dd className="font-semibold text-blue-600 tabular-nums">
                {Number(job.payload.ownershipChangesDetected).toLocaleString()}
              </dd>
            </div>
          )}
          {typeof job.payload.matchedCount === 'number' && (
            <div>
              <dt className="font-medium mb-0.5" style={{ color: theme.textSecondary }}>Matched</dt>
              <dd className="font-semibold tabular-nums" style={{ color: theme.textPrimary }}>
                {Number(job.payload.matchedCount).toLocaleString()}
              </dd>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
